/**
 * Realtime configuration (TO-468, ADR-0049). FAILS CLOSED: unless all three
 * `VITE_REALTIME_*` values are present and the prefix is a well-formed
 * `{app}/{stage}`, the config is `null`, no connection is attempted and every
 * screen keeps polling. A malformed value never throws.
 */
import { isValidTopicPrefix } from '@today/types'
import { type RealtimeEnv, realtimeEnv } from './realtime-env'

export interface RealtimeConfig {
	/** IoT ATS endpoint host, no scheme. */
	endpoint: string
	/** Name of the IoT custom authorizer. */
	authorizer: string
	/** `{app}/{stage}` — the isolation boundary between deployments. */
	topicPrefix: string
	app: string
	stage: string
}

/** Per-field status shown on the diagnostics page. */
export enum RealtimeEnvFieldStatus {
	OK = 'ok',
	MISSING = 'missing',
	MALFORMED = 'malformed',
}

export interface RealtimeEnvField {
	status: RealtimeEnvFieldStatus
	value?: string
}

const present = (value: string | undefined): string | undefined => {
	const trimmed = value?.trim()
	return trimmed ? trimmed : undefined
}

const field = (value: string | undefined, valid = true): RealtimeEnvField => {
	const v = present(value)
	if (!v) return { status: RealtimeEnvFieldStatus.MISSING }
	return { status: valid ? RealtimeEnvFieldStatus.OK : RealtimeEnvFieldStatus.MALFORMED, value: v }
}

/** Per-field status of the realtime env, for the operator diagnostics view. */
export function describeRealtimeEnv(
	env: RealtimeEnv,
): Record<'endpoint' | 'authorizer' | 'topicPrefix', RealtimeEnvField> {
	const prefix = present(env.VITE_REALTIME_TOPIC_PREFIX)
	return {
		endpoint: field(env.VITE_REALTIME_ENDPOINT),
		authorizer: field(env.VITE_REALTIME_AUTHORIZER),
		topicPrefix: field(prefix, prefix !== undefined && isValidTopicPrefix(prefix)),
	}
}

/** The realtime config, or `null` (polling only) when anything is absent or malformed. */
export function readRealtimeConfig(env: RealtimeEnv): RealtimeConfig | null {
	const { endpoint, authorizer, topicPrefix } = describeRealtimeEnv(env)
	if (
		endpoint.status !== RealtimeEnvFieldStatus.OK ||
		authorizer.status !== RealtimeEnvFieldStatus.OK ||
		topicPrefix.status !== RealtimeEnvFieldStatus.OK
	) {
		return null
	}
	const prefix = topicPrefix.value as string
	const [app, stage] = prefix.split('/')
	return { endpoint: endpoint.value as string, authorizer: authorizer.value as string, topicPrefix: prefix, app, stage }
}

/** This bundle's realtime config (`null` → polling only). */
export const realtimeConfig: RealtimeConfig | null = readRealtimeConfig(realtimeEnv())
